import {
  type UpdateUserResponse,
  type UpdateUserRequest,
  type GenerateLicenseResponse,
  type GetUserWordsResponse,
  type DowngradeSubscriptionResponse,
  type CancelSubscriptionResponse,
  type ApiResponse,
  type GetCollectionData,
  type CheckoutResult,
  type SubscriptionOperationSuccess,
  type User,
  type GetUserSubscriptionResponse,
  type OverviewStatsResponse,
  type ProgressResponse,
  type InteractionsResponse,
  type DisplayWordCollection,
  UpgradeSubscriptionRequestSchema,
  DowngradeSubscriptionRequestSchema,
  ValidationPatterns,
  LeadsAPI,
} from '@lingugeek/schemas';

import {
  validateRequest,
  ValidationError,
  buildAudioUrl,
  type AudioType,
} from '@lingugeek/shared';
import { API_ENDPOINTS } from '@/config/api';
import { UpdateUserAPI } from '@lingugeek/schemas';

type UnauthorizedHandler = () => void | Promise<void>;

let unauthorizedHandler: UnauthorizedHandler | null = null;

export function setAuthHandlers(
  handlers: {
    onUnauthorized?: UnauthorizedHandler;
  } = {}
) {
  if (handlers.onUnauthorized) unauthorizedHandler = handlers.onUnauthorized;
}

async function apiRequest<T>(
  url: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 401 && unauthorizedHandler) {
        unauthorizedHandler();
      }
      return {
        success: false,
        data: null,
        message: data.message || 'Request failed',
        error: {
          message:
            data.error?.message || data.error || `HTTP ${response.status}`,
          code: data.error?.code || 'UNKNOWN_ERROR',
          details: data.error?.details,
        },
      };
    }

    return {
      success: true,
      data: data.data,
      message: data.message || 'Request successful',
      meta: data.meta,
    };
  } catch (error) {
    return {
      success: false,
      data: null,
      message: 'Network error occurred',
      error: {
        message: error instanceof Error ? error.message : 'Network error',
        code: 'NETWORK_ERROR',
      },
    };
  }
}

function handleApiResponse<T>(response: ApiResponse<T>): T {
  if (!response.success) {
    throw new Error(response.error?.message || 'API request failed');
  }

  if (response.data === null || response.data === undefined) {
    throw new Error('No data received from API');
  }

  return response.data;
}

const IdRequestSchema = ValidationPatterns.objectId;

export const api = {
  getUser: async (): Promise<User> => {
    const response = await apiRequest<User>(API_ENDPOINTS.USER_ME);
    return handleApiResponse(response);
  },

  updateUser: async (data: UpdateUserRequest): Promise<UpdateUserResponse> => {
    const requestValidation = validateRequest(UpdateUserAPI.request, data);
    if (!requestValidation.success) {
      throw new ValidationError(
        'Invalid user update data',
        requestValidation.error.details
      );
    }

    const response = await apiRequest<UpdateUserResponse>(
      API_ENDPOINTS.USER_UPDATE,
      {
        method: 'PUT',
        body: JSON.stringify(requestValidation.data),
      }
    );
    return handleApiResponse(response);
  },

  logout: async (): Promise<{ message: string }> => {
    const response = await apiRequest<{ message: string }>(
      API_ENDPOINTS.AUTH_LOGOUT,
      {
        method: 'POST',
      }
    );
    return handleApiResponse(response);
  },

  getWords: async (query = ''): Promise<GetUserWordsResponse> => {
    const response = await apiRequest<GetUserWordsResponse>(
      `${API_ENDPOINTS.WORDS}${query}`
    );
    return handleApiResponse(response);
  },

  getSubscription: async (): Promise<GetUserSubscriptionResponse> => {
    const response = await apiRequest<GetUserSubscriptionResponse>(
      API_ENDPOINTS.SUBSCRIPTION_ME
    );
    return handleApiResponse(response);
  },

  upgradeSubscription: async (
    plan: 'pro' | 'ultra'
  ): Promise<CheckoutResult | SubscriptionOperationSuccess> => {
    const requestData = { plan };
    const requestValidation = validateRequest(
      UpgradeSubscriptionRequestSchema,
      requestData
    );
    if (!requestValidation.success) {
      throw new ValidationError(
        'Invalid subscription upgrade data',
        requestValidation.error.details
      );
    }

    const response = await apiRequest<
      CheckoutResult | SubscriptionOperationSuccess
    >(API_ENDPOINTS.SUBSCRIPTION_UPGRADE, {
      method: 'POST',
      body: JSON.stringify(requestValidation.data),
    });
    return handleApiResponse<CheckoutResult | SubscriptionOperationSuccess>(
      response
    );
  },

  downgradeSubscription: async (
    plan: 'free' | 'pro'
  ): Promise<DowngradeSubscriptionResponse> => {
    const requestData = { plan };
    const requestValidation = validateRequest(
      DowngradeSubscriptionRequestSchema,
      requestData
    );
    if (!requestValidation.success) {
      throw new ValidationError(
        'Invalid subscription downgrade data',
        requestValidation.error.details
      );
    }

    const response = await apiRequest<DowngradeSubscriptionResponse>(
      API_ENDPOINTS.SUBSCRIPTION_DOWNGRADE,
      {
        method: 'POST',
        body: JSON.stringify(requestValidation.data),
      }
    );
    return handleApiResponse(response);
  },

  cancelSubscription: async (): Promise<CancelSubscriptionResponse> => {
    const response = await apiRequest<CancelSubscriptionResponse>(
      API_ENDPOINTS.SUBSCRIPTION_CANCEL,
      {
        method: 'POST',
      }
    );
    return handleApiResponse(response);
  },

  getStats: async (): Promise<OverviewStatsResponse> => {
    const response = await apiRequest<OverviewStatsResponse>(
      API_ENDPOINTS.STATS
    );
    return handleApiResponse(response);
  },

  getProgress: async (): Promise<ProgressResponse> => {
    const response = await apiRequest<ProgressResponse>(
      API_ENDPOINTS.STATS_PROGRESS
    );
    return handleApiResponse(response);
  },

  getInteractions: async (
    limit = 5,
    opts: { journeyOnly?: boolean } = {}
  ): Promise<InteractionsResponse> => {
    const journeyOnly = opts.journeyOnly ? 'true' : 'false';
    const response = await apiRequest<InteractionsResponse>(
      `${API_ENDPOINTS.STATS_INTERACTIONS}?limit=${limit}&journeyOnly=${journeyOnly}`
    );
    return handleApiResponse(response);
  },

  generateLicense: async (): Promise<GenerateLicenseResponse> => {
    const response = await apiRequest<GenerateLicenseResponse>(
      API_ENDPOINTS.USER_LICENSE,
      {
        method: 'POST',
      }
    );
    return handleApiResponse(response);
  },

  createLead: async (email: string): Promise<{ message: string }> => {
    const requestData = { email };
    const requestValidation = validateRequest(
      LeadsAPI.subscription.request,
      requestData
    );
    if (!requestValidation.success) {
      throw new ValidationError(
        'Invalid lead creation data',
        requestValidation.error.details
      );
    }

    const response = await apiRequest<{ message: string }>(
      API_ENDPOINTS.LEADS,
      {
        method: 'POST',
        body: JSON.stringify(requestValidation.data),
      }
    );
    return handleApiResponse(response);
  },

  getLearnHistory: async (): Promise<DisplayWordCollection[]> => {
    const response = await apiRequest<DisplayWordCollection[]>(
      API_ENDPOINTS.LEARN_HISTORY
    );
    return handleApiResponse(response);
  },

  getLearnCollection: async (id: string): Promise<GetCollectionData> => {
    const requestValidation = validateRequest(IdRequestSchema, id);
    if (!requestValidation.success) {
      throw new ValidationError(
        'Invalid collection ID',
        requestValidation.error.details
      );
    }

    const response = await apiRequest<GetCollectionData>(
      `${API_ENDPOINTS.LEARN_COLLECTION}?id=${requestValidation.data}`
    );
    return handleApiResponse<GetCollectionData>(response);
  },

  getAudioUrl: (wordId: string, type: AudioType): string => {
    const wordIdValidation = validateRequest(
      ValidationPatterns.objectId,
      wordId
    );
    if (!wordIdValidation.success) {
      throw new ValidationError(
        'Invalid word ID format',
        wordIdValidation.error.details
      );
    }

    return buildAudioUrl(
      API_ENDPOINTS.AUDIO_STREAM,
      wordIdValidation.data as string,
      type
    );
  },
};
